import RequestHelper from "./request_helper"

// Only used to namespace the select2 change binding below - see the comment on that line.
const NS = ".contacts";

export function fetchJson(url, options) {
  return fetch(url, options).then((response) => response.json().then((data) => ({ ok: response.ok, data })));
}

// The subdomain/contacts view/add/edit drawer. Extracted out of contacts_controller.js (R9 -
// this owns its own state, independent of the Stimulus controller's datatable/bulk-selection
// state). The partial it fetches carries its own `validation` controller, which Stimulus wires up
// automatically as soon as `render()` inserts the HTML - nothing here needs to re-scan the
// inserted markup.
//
// This is a plain JS class, not a Stimulus controller - it can't declare Stimulus targets or use
// the Classes API, because its element lookups query into server-rendered content that lives
// outside any Stimulus controller's own element tree (see the querySelector/classList calls
// throughout initializeForm()).
//
// `element` is a native element. `signal` is the owning controller's AbortController signal, so
// the document-level listeners bound in initializeForm() are torn down together with the
// controller's own listeners on disconnect() - the same lifetime the shared jQuery `NS` namespace
// used to give them.
export default class ContactDrawer {
  constructor(element, { signal } = {}) {
    this.element = element;
    this.signal = signal;
  }

  open(contactUrl) {
    this.element.classList.add("active");

    const options = RequestHelper.defaultRequestOptions();
    options.headers = Object.assign({}, options.headers, {
      "Content-Type": "application/json",
      "X-Requested-With": "XMLHttpRequest",
      "Accept": "application/json, text/plain, */*",
    });

    fetchJson(contactUrl, options).then(({ ok, data }) => {
      if (!ok) {
        this.element.classList.remove("active");
        window.ltAlert({ message: data.message, type: "toast-error", duration: 2500 });
        return;
      }
      this.render(data.html);
    });
  }

  render(html) {
    // S4: `html` is rendered server-side by Subdomain::ContactsController#show/#new/#edit/#update
    // via render_to_string(partial: "contact_drawer" | "contact_form", ...) - same origin, and
    // neither partial uses raw/html_safe/`!=`, so every interpolated contact field (name, email,
    // notes) is Haml-escaped. Not user-supplied markup - safe to inject directly.
    this.element.innerHTML = html;
    this.element.classList.add("active");

    // Deferred a tick so the CSS transition actually runs.
    setTimeout(() => {
      Array.from(this.element.children).forEach((child) => child.classList.remove("closed"));
    }, 5);

    if (this.element.querySelector(".contact-form")) {
      this.initializeForm();
    }

    this.element.querySelectorAll("[data-toggle='close']").forEach((el) => {
      el.addEventListener("click", () => this.close());
    });
  }

  close() {
    this.element.classList.remove("active");
    Array.from(this.element.children).forEach((child) => child.classList.add("closed"));
  }

  initializeForm() {
    const contactListsSelector = this.element.querySelector("#contact_contact_list_ids");
    const birthdayPicker = this.element.querySelector("#contact_birthday");
    const workAnniversaryPicker = this.element.querySelector("#contact_work_anniversary");
    const addCustomDateLink = this.element.querySelector("#save-custom-date");
    const deleteCustomDateLink = this.element.querySelector("#delete-custom-date");
    const additionalCustomDateLink = this.element.querySelector("#additional-custom-date");
    const additionalDateForm = this.element.querySelector("#additional-custom-date-form");
    const newCustomDateNameInput = this.element.querySelector("#new_custom_date_name");
    const newCustomDateNameContainer = this.element.querySelector("#new-custom-date-name-container");
    const newCustomDateNameValidationContainer = this.element.querySelector("#new-custom-date-name-validation-container");
    const contactCustomDatePickers = this.element.querySelectorAll(".contact-custom-date-picker");

    const datetimepickerConfig = { ...window.datetimepickerDefaults, format: "MM/DD/YYYY" };

    // datetimepicker and select2 are jQuery plugins; the element lookups above are native, only
    // the plugin calls themselves stay on jQuery.
    // maxDate is end of day, not the current instant - see account_edit_controller.js.
    if (birthdayPicker) { $(birthdayPicker).datetimepicker({ maxDate: moment().endOf("day"), ...datetimepickerConfig }); }
    if (workAnniversaryPicker) { $(workAnniversaryPicker).datetimepicker(datetimepickerConfig); }
    contactCustomDatePickers.forEach((el) => $(el).datetimepicker(datetimepickerConfig));

    contactCustomDatePickers.forEach((el) => {
      if (!el.getAttribute("value")) { el.value = ""; }
    });

    if (newCustomDateNameInput) {
      newCustomDateNameInput.addEventListener("input", (e) => {
        const value = e.target.value.trim();
        if (value.length > 0) {
          if (addCustomDateLink) { addCustomDateLink.disabled = false; }
          if (newCustomDateNameValidationContainer) { newCustomDateNameValidationContainer.style.display = "none"; }
          if (newCustomDateNameContainer) { newCustomDateNameContainer.classList.remove("has-error"); }
        } else if (addCustomDateLink) {
          addCustomDateLink.disabled = true;
        }
      }, { signal: this.signal });

      newCustomDateNameInput.addEventListener("keypress", (e) => {
        if (e.which === 13) {
          e.preventDefault();
          if (e.target.value.trim().length > 0 && addCustomDateLink) { addCustomDateLink.click(); }
        }
      }, { signal: this.signal });
    }

    // Delegated on document because the edit-in-place rows are re-rendered inside the drawer on
    // every render() call, same as the original jQuery delegation.
    document.addEventListener("input", (e) => {
      if (!e.target.matches("[id^='edit_custom_date_name_']")) { return; }
      const input = e.target;
      const customDateId = input.id.replace("edit_custom_date_name_", "");
      const updateButton = document.getElementById(`update-custom-date-${customDateId}`);
      const container = document.getElementById(`edit-custom-date-name-container-${customDateId}`);
      const validationContainer = document.getElementById(`edit-custom-date-name-validation-container-${customDateId}`);

      if (input.value.trim().length > 0) {
        if (updateButton) { updateButton.disabled = false; }
        if (validationContainer) { validationContainer.style.display = "none"; }
        if (container) { container.classList.remove("has-error"); }
      } else if (updateButton) {
        updateButton.disabled = true;
      }
    }, { signal: this.signal });

    document.addEventListener("keypress", (e) => {
      if (!e.target.matches("[id^='edit_custom_date_name_']")) { return; }
      if (e.which === 13) {
        e.preventDefault();
        const input = e.target;
        const customDateId = input.id.replace("edit_custom_date_name_", "");
        const updateButton = document.getElementById(`update-custom-date-${customDateId}`);
        if (input.value.trim().length > 0 && updateButton && !updateButton.disabled) { updateButton.click(); }
      }
    }, { signal: this.signal });

    if (additionalCustomDateLink) {
      additionalCustomDateLink.addEventListener("click", (e) => {
        e.preventDefault();
        if (additionalDateForm) { additionalDateForm.style.display = ""; }
        if (newCustomDateNameInput) { newCustomDateNameInput.value = ""; }
        additionalCustomDateLink.style.display = "none";
      }, { signal: this.signal });
    }

    if (deleteCustomDateLink) {
      deleteCustomDateLink.addEventListener("click", (e) => {
        e.preventDefault();
        if (additionalDateForm) { additionalDateForm.style.display = "none"; }
        if (additionalCustomDateLink) { additionalCustomDateLink.style.display = ""; }
      }, { signal: this.signal });
    }

    if (addCustomDateLink) {
      addCustomDateLink.addEventListener("click", (e) => {
        e.preventDefault();
        const url = additionalDateForm ? additionalDateForm.dataset.url : undefined;
        const name = newCustomDateNameInput ? newCustomDateNameInput.value : "";
        const contactId = e.currentTarget.dataset.contactId;

        const options = RequestHelper.defaultPostOptions();
        options.headers = Object.assign({}, options.headers, {
          "Content-Type": "application/json",
          "X-Requested-With": "XMLHttpRequest",
          "Accept": "application/json, text/plain, */*",
        });
        options.body = JSON.stringify({ contact_custom_date: { name: name }, contact: { id: contactId } });

        fetchJson(url, options).then(({ ok, data }) => {
          if (!ok) {
            if (newCustomDateNameContainer) { newCustomDateNameContainer.classList.add("has-error"); }
            if (newCustomDateNameValidationContainer) {
              const icon = newCustomDateNameValidationContainer.querySelector("i");
              if (icon) { icon.insertAdjacentText("afterend", ` ${data.errors.join(', ')}`); }
              newCustomDateNameValidationContainer.style.display = "";
            }
            return;
          }
          this.render(data.html);
        });
      }, { signal: this.signal });
    }

    // Unscoped, matching the original `$(".edit-custom-date-link")` lookup (jQuery with no
    // context searches the whole document, not just this.element).
    document.querySelectorAll(".edit-custom-date-link").forEach((link) => {
      link.addEventListener("click", (e) => {
        e.preventDefault();
        const customDateId = e.currentTarget.dataset.customDateId;
        const customDateName = e.currentTarget.dataset.customDateName;
        const customDateRow = e.currentTarget.closest(".row");
        const editForm = document.getElementById(`custom-date-edit-${customDateId}`);
        const editInput = editForm ? editForm.querySelector(`#edit_custom_date_name_${customDateId}`) : null;

        if (customDateRow) { customDateRow.style.display = "none"; }
        if (editForm) { editForm.style.display = ""; }
        if (editInput) { editInput.value = customDateName; editInput.focus(); }
      }, { signal: this.signal });
    });

    document.addEventListener("click", (e) => {
      const cancelLink = e.target.closest("[id^='cancel-edit-custom-date']");
      if (!cancelLink) { return; }
      e.preventDefault();
      const editForm = cancelLink.closest(".custom-date-edit-form");
      const customDateRow = editForm ? editForm.previousElementSibling : null;
      if (editForm) { editForm.style.display = "none"; }
      if (customDateRow && customDateRow.matches(".row")) { customDateRow.style.display = ""; }
    }, { signal: this.signal });

    document.addEventListener("click", (e) => {
      const updateLink = e.target.closest("[id^='update-custom-date']");
      if (!updateLink) { return; }
      e.preventDefault();
      const editForm = updateLink.closest(".custom-date-edit-form");
      const customDateId = updateLink.dataset.customDateId;
      const contactId = updateLink.dataset.contactId;
      const editInput = editForm ? editForm.querySelector(`#edit_custom_date_name_${customDateId}`) : null;
      const newName = editInput ? editInput.value.trim() : "";

      if (newName.length === 0) { return; }

      const url = `/contact/custom_dates/${customDateId}`;
      const options = RequestHelper.defaultPatchOptions();
      options.headers = Object.assign({}, options.headers, {
        "Content-Type": "application/json",
        "X-Requested-With": "XMLHttpRequest",
        "Accept": "application/json, text/plain, */*",
      });
      options.body = JSON.stringify({ contact_custom_date: { name: newName }, contact: { id: contactId } });

      fetchJson(url, options).then(({ ok, data }) => {
        if (!ok) {
          const errorContainer = editForm ? editForm.querySelector("[id$='validation-container']") : null;
          const formGroup = editForm ? editForm.querySelector("[id$='name-container']") : null;
          if (formGroup) { formGroup.classList.add("has-error"); }
          if (errorContainer) {
            const icon = errorContainer.querySelector("i");
            if (icon) { icon.insertAdjacentText("afterend", ` ${data.errors.join(', ')}`); }
            errorContainer.style.display = "";
          }
          return;
        }
        this.render(data.html);
      });
    }, { signal: this.signal });

    const setSelectionColorClasses = () => {
      Array.from(contactListsSelector.selectedOptions).forEach((option) => {
        const colorClass = option.dataset.color;
        const drawer = document.getElementById("contacts-drawer");
        if (!drawer) { return; }
        drawer.querySelectorAll(`.select2-selection__choice[title='${CSS.escape(option.textContent)}']`).forEach((el) => el.classList.add(colorClass));
      });
    };

    if (contactListsSelector) {
      // select2 plugin - stays jQuery.
      $(contactListsSelector).select2({ placeholder: "Select contact lists", width: "100%", allowClear: false, multiple: true });
      setSelectionColorClasses();
      $(contactListsSelector).on(`change${NS}`, () => setSelectionColorClasses());
    }
  }
}
